import { Observable, observable } from "../signal/Signal";
import View from "../view/View";


export function node(html: string): NodeWrapper;
export function node(view: View): NodeWrapper;
export function node(native: Node): NodeWrapper;
export function node(arg: string | View | Node): NodeWrapper {
	if (typeof arg === 'string') {

		// wrap the content into new node because it can be one root node in template or its content
		let temp = document.createElement('div' );
		temp.innerHTML = arg;

		// If there is exactly one child the template has one root node - return this one.
		// otherwise if there are many or no children return the temp div as the root.
		return new NodeWrapper( ( temp.children.length == 1 ) ? temp.children[0] : temp );
	}
	else if (arg instanceof Node) {
		return new NodeWrapper( arg as Element );
	}
	else if ('node' in arg && arg['node'] instanceof Node) {
		return new NodeWrapper( arg.node );
	}
	else {
		return null;
	}
}


class NodeWrapper {

	constructor( public native:Element ) {}

	get clicks() {
		return observable( 'click', this.native );
	}
	get scrolls() {
		return observable( 'scroll', this.native );
	}
	get mouseleaves() {
		return observable( 'mouseleave', this.native );
	}
	get mouseenters() {
		return observable( 'mouseenter', this.native );
	}
	get mouseouts() {
		return observable( 'mouseout', this.native );
	}
	get mouseovers() {
		return observable( 'mouseover', this.native );
	}
	get mouseups() {
		return observable( 'mouseup', this.native );
	}
	get mousedowns() {
		return observable( 'mousedown', this.native );
	}
	get mousemoves() {
		return observable( 'mousemove', this.native );
	}
	get keypresses() {
		return observable( 'keypress', this.native );
	}
	get keydowns() {
		return observable( 'keydown', this.native );
	}
	get keyups() {
		return observable( 'keyup', this.native );
	}
	get focuses() {
		return observable( 'focus', this.native );
	}
	get blurs() {
		return observable( 'blur', this.native );
	}
	get changes() {
		return observable( 'change', this.native );
	}

	/**
	 * Removes all element within the native node
	 */
	empty() {
		for ( var i = this.native.children.length; i--; ) {
			this.native.removeChild( this.native.children[i] );
		}
	}

	/**
	 * Removes the given element from the dom.
	 */
	remove() {
		if( this.native.parentElement ) this.native.parentElement.removeChild( this.native );
	}

	/**
	 * Replaces this native node with the given newNode
	 * @param newNode The new node to be place on position of the given existingNode
	 */
	replace( newNode:Node ) {
		this.native.parentNode.replaceChild( newNode, this.native );
	}

	/**
	 * Prepends the given node to this node
	 * @param node the new node to prepend
	 */
	prepend( node:Node ) {
		this.native.insertBefore( node, this.native.firstChild );
	}


	/**
	 * Places a node at the given index
	 * @param node The new node to place
	 * @param index The index where to place the node
	 */
	insert( node:Node, index:number ) {
		this.native.insertBefore( node, this.native.childNodes[ index ] );
	}


	/**
	 * Check if the classname exists in the classlist of this node
	 *
	 * @param classname Classname to be checked
	 * @return `true` if the class exists, `false` else
	 */
	hasClass( classname: string ): boolean {
		return this.getClasses().indexOf( classname ) > -1;
	}


	/**
	 * Adds a css class the the class list
	 * @param name The name of the class to add
	 */
	addClass( name:string ) {
		this.setClasses( this.getClasses().concat( [ name ] ) );
	}

	/**
	 * Removes a class from the list
	 * @param name The css class to remove
	 */
	removeClass( name:string ) {
		this.setClasses( this.getClasses().filter( current => name != current ) );
	}

	/**
	 * Toggles a given class name on the node
	 * @param name the name of the css class to toggle
	 * @param force a flag to either force to add the class ( true ) or to remove the class (false)
	 */
	toggleClass( name:string, force?:boolean ) {
		if( force == undefined ) {
			( this.hasClass( name ) ) ? this.removeClass( name ) : this.addClass( name );
		} else {
			( force ) ? this.addClass( name ) : this.removeClass( name );
		}
	}

	/**
	 * Helper function to set classes distinct
	 */
	private setClasses( classList:string[] ) {
		let distinct = classList.filter( ( name, index, list ) => name.length > 0  && list.indexOf( name ) === index );
		this.native.setAttribute( 'class', distinct.join(' ') );
	}

	private getClasses():string[] {
		let list = this.native.getAttribute('class');
		return ( list ) ? list.split( ' ' ) : [];
	}


	/**
	 * Returns the bounding box of this node including values for top, bottom, left, right relative to the current viewport
	 */
	relativeBounds() {
		let c = this.native.getBoundingClientRect();
		let r = {  top: c.top, left: c.left, right: c.right, bottom: c.bottom, height: c.height, width: c.width };

		if ( r.height == undefined ) r.height = r.bottom - r.top;
		if ( r.width == undefined ) r.width = r.right - r.left;

		return r;
	}

	/**
	 * Returns the bounding box of this node including values for top, bottom, left, right in absolute document coordinates
	 */
	absoluteBounds() {
		let box = this.relativeBounds();

		box.top += document.body.scrollTop || document.documentElement.scrollTop;
		box.bottom += document.body.scrollTop || document.documentElement.scrollTop;
		box.left += document.body.scrollLeft || document.documentElement.scrollLeft;
		box.right += document.body.scrollLeft || document.documentElement.scrollLeft;

		return box;
	}
}
